import React, { useEffect } from 'react';
import { SITE } from '@/constants/testIds';
import { t } from '@/lib/i18n';
import { applyDocumentLang } from '@/lib/langPreference';
import { MEDARD_ENGRAVING_SRC_SMALL } from '@/components/ui/MedardOwlEngraving';

/**
 * WideScreenOnly — ce qu'un téléphone voit devant une page du site qui n'a
 * AUCUNE adresse dans l'app (un conte, la Bibliothèque, une suite ;
 * lib/siteRoutes.SITE_WIDE_ONLY).
 *
 * ⚑ Jusqu'au 10/09/2026, App.js réécrivait ces adresses en `/` par
 * `replaceState` et rendait l'accueil de Médard : le lien d'un conte partagé
 * par messagerie ouvrait la modale d'accueil, l'adresse effacée de la barre,
 * sans un mot. La note remplace le silence : l'adresse est GARDÉE (l'invité
 * peut la copier, la rouvrir sur un ordinateur), la page dit où elle se lit,
 * et une seule porte mène à l'app — un vrai lien, pas une réécriture.
 *
 * Volontairement dans le tronc commun (pas dans le chunk du site) : elle pèse
 * quelques lignes, et un téléphone ne télécharge jamais le site.
 */
export default function WideScreenOnly({ lang = 'fr' }) {
    useEffect(() => { applyDocumentLang(lang); }, [lang]);
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const previous = document.title;
        document.title = `${t(lang, 'siteWideOnlyTitle')} · Médard`;
        return () => { document.title = previous; };
    }, [lang]);
    const home = lang && lang !== 'fr' ? `/?lang=${encodeURIComponent(lang)}` : '/';
    return (
        <main
            data-testid={SITE.wideOnly}
            className="flex min-h-screen w-full flex-col items-center justify-center bg-aman-ivory px-6 py-16 text-center text-manoir-ink font-body"
        >
            <img src={MEDARD_ENGRAVING_SRC_SMALL} alt="" aria-hidden="true" draggable="false" className="h-28 w-auto opacity-80" />
            <h1 className="mt-8 max-w-[24ch] font-serif text-[30px] font-light leading-tight text-manoir-delft-deep [text-wrap:balance]">
                {t(lang, 'siteWideOnlyTitle')}
            </h1>
            <p className="mt-4 max-w-[36ch] font-serif text-[17px] italic leading-relaxed text-manoir-ink-soft">
                {t(lang, 'siteWideOnlyLede')}
            </p>
            <a
                href={home}
                data-testid={SITE.wideOnlyHome}
                className="mt-8 inline-flex min-h-[44px] items-center border border-manoir-ink px-5 text-[11.5px] font-medium uppercase tracking-[0.26em] text-manoir-ink motion-safe:transition-colors hover:bg-manoir-ink/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-manoir-delft"
            >
                {t(lang, 'siteWideOnlyHome')}
            </a>
        </main>
    );
}
